/**
 * Live bed-count stream client (M5-T03). The backend publishes `bed_update`
 * events on the `/api/v1/live` WebSocket (M5-T02) whenever a hospital admin
 * commits a bed change; the `useLiveBeds` hook patches the React Query cache so
 * badges update without a refetch.
 */
import { env } from '../lib/env'
import type { BedState } from './hospitals'

/** The wire envelope the backend broadcasts (see `app/lib/events.py`). */
export type BedUpdateEvent = {
  type: 'bed_update'
  hospital_id: string
  beds: BedState
}

/** Derive the WebSocket URL from the HTTP API base: http→ws, https→wss. */
export function liveBedsUrl(): string {
  const base = env.apiBaseUrl.replace(/^http/, 'ws')
  return `${base}/api/v1/live`
}

/** Parse a raw frame into a `BedUpdateEvent`, or `null` if it's malformed or
 * an unrecognised type. Never throws — a bad frame is simply ignored. */
export function parseBedUpdate(raw: string): BedUpdateEvent | null {
  try {
    const msg = JSON.parse(raw) as Partial<BedUpdateEvent>
    if (
      msg?.type === 'bed_update' &&
      typeof msg.hospital_id === 'string' &&
      msg.beds != null
    ) {
      return msg as BedUpdateEvent
    }
  } catch {
    // non-JSON or unexpected shape — ignore
  }
  return null
}
